import { useState } from "react";
import { useUpdateOrderStatus } from "../../hooks/useOrders";
import type { Order } from "../../services/ordersApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNotification } from "@/shared/hooks/useNotification";

const STATUS_FLOW = [
  "Placed",
  "Confirmed",
  "SowingInitiated",
  "Germinated",
  "Growing",
  "ProductionCompleted",
  "Stored",
  "ReadyForDispatch",
  "PartiallyDispatched",
  "Dispatched",
  "Delivered",
];

interface UpdateOrderStatusDialogProps {
  order: Order;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UpdateOrderStatusDialog = ({
  order,
  open,
  onOpenChange,
}: UpdateOrderStatusDialogProps) => {
  const [newStatus, setNewStatus] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");

  const updateMutation = useUpdateOrderStatus();
  const notification = useNotification();

  const currentStatusIndex = STATUS_FLOW.indexOf(order.status);
  const availableStatuses = STATUS_FLOW.slice(currentStatusIndex + 1);

  const handleSubmit = () => {
    if (!newStatus || !reason) {
      notification.error("Please fill in all required fields");
      return;
    }

    updateMutation.mutate(
      {
        orderId: order.id,
        newStatus,
        reason,
        notes,
      },
      {
        onSuccess: () => {
          setNewStatus("");
          setReason("");
          setNotes("");
          onOpenChange(false);
          notification.success("Order status updated successfully");
        },
        onError: () => {
          notification.error("Failed to update order status. Please try again.");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update Order Status</DialogTitle>
          <DialogDescription>
            Current: {order.status} → New Status
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field>
            <FieldLabel>New Status *</FieldLabel>
            <Select
              value={newStatus}
              onValueChange={setNewStatus}
              disabled={updateMutation.isPending}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {availableStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Reason *</FieldLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is the status being updated?"
              disabled={updateMutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes (optional)"
              disabled={updateMutation.isPending}
            />
          </Field>

          <div className="flex gap-2 justify-end pt-4">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Updating..." : "Update Status"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
